/**
 * 결제수단 → 결제 계좌 소유자(spenderId) 자동 결정 헬퍼
 *
 * @why 자동선택 규칙을 폼에서 분리해 순수 함수로 단위 테스트하기 위함.
 *      TransactionForm은 비동기 훅으로 데이터를 로드하고 native <select> 기반이라
 *      DOM 테스트가 취약하므로, 도메인 규칙만 순수 함수로 빼서 검증한다.
 */

import type { PaymentMethod } from '@/domains/payment/types/PaymentMethod'

/**
 * 선택한 결제수단으로부터 "결제 계좌 소유자"(코드상 spenderId)를 결정한다.
 *
 * 도메인 규칙: 이 값은 "카드값이 출금되는 계좌의 소유자"를 뜻하며 결제수단에 종속된다.
 * 결제수단 설정에서 정한 소유자(ownerId)가 곧 결제 계좌 소유자다.
 *  - 비현금(카드/간편결제/계좌이체): ownerId 반환. 공용 카드면 ownerId === 'user_shared'.
 *  - 현금: 결제수단만으로 누가 부담하는지 특정할 수 없어 빈 문자열 반환 → 사용자가 직접 선택.
 *  - 미선택(undefined): 빈 문자열.
 *
 * 반환값은 spenderId 세팅값으로 쓰이며, 비현금이라도 사용자가 이후 수동 변경 가능하다.
 * (현금 특수 케이스 때문에 boolean이 아닌 "세팅할 값"을 직접 반환하도록 설계)
 *
 * @param paymentMethod 선택된 결제수단(미선택 시 undefined)
 * @returns spenderId로 세팅할 값. 자동선택 불가/현금/미선택이면 ''.
 */
export function resolveSpenderForPaymentMethod(
  paymentMethod: PaymentMethod | undefined
): string {
  if (!paymentMethod) return ''
  // 현금은 소유자가 있어도 실제 부담자를 특정할 수 없으므로 수동 선택 강제
  if (paymentMethod.type === 'cash') return ''
  return paymentMethod.ownerId
}
